'use client';

import { useState } from 'react';

import { CheckCircle, Minus, XCircle } from 'lucide-react';

import { BenefitsSection } from '@/components/sections/benefits-section';
import { FaqSection } from '@/components/sections/faq-section';
import { HelpdeskHero } from '@/components/sections/helpdesk-hero';
import { IndustryCustomers } from '@/components/sections/industry-customers';
import Logos from '@/components/sections/logos';
import Testimonials from '@/components/sections/testimonials';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useTranslation } from '@/lib/i18n/context';
import { INDUSTRY_FAQS } from '@/lib/industry-faqs';
import { cn } from '@/lib/utils';

const CONTENT = {
  en: {
    heroTitleA: 'Care that',
    heroTitleB: "doesn't clock out",
    heroSubtitle:
      'Give your patients 24/7 answers about appointments, insurance, and prescriptions—securely. Agent 5 handles the admin work so your team can focus on care.',
    heroCta: 'See how it works for healthcare',
    benefitsTitle: 'Support patients better, protect their privacy',
    benefitsSubtitle:
      'Handle the support issues that matter most in healthcare—securely and compliantly.',
    benefits: [
      {
        title: 'Appointment scheduling and changes',
        description:
          'Patients book appointments, request reschedules, and get reminders—instantly, any time. Integrates with your scheduling system. No more phone tag.',
      },
      {
        title: 'Insurance eligibility and coverage questions',
        description:
          "Is this procedure covered? What's my copay? Which providers accept my insurance? Agent 5 verifies coverage in real-time so patients know before they book.",
      },
      {
        title: 'Prescription refills and medication questions',
        description:
          'Patients request refills, check prescription status, and ask about side effects or dosage. Agent 5 processes requests, tracks status, and escalates medical questions appropriately.',
      },
      {
        title: 'Billing inquiries and payment questions',
        description:
          'What does my insurance cover? Why was I charged this? When is payment due? Agent 5 explains bills clearly, processes payments, and sets up payment plans—reducing billing confusion.',
      },
      {
        title: 'Test results and follow-up care',
        description:
          'Are my results ready? What happens next? Agent 5 notifies patients when results are available, explains next steps, and schedules follow-ups—keeping care moving forward.',
      },
      {
        title: 'Provider directory and referrals',
        description:
          'Help patients find specialists, check provider availability, and understand referral requirements. Agent 5 guides them to the right care, faster.',
      },
      {
        title: 'Privacy and security by design',
        description:
          "All patient data encrypted. Automatic PHI detection. Complete audit trails. We're building toward HIPAA compliance—your security requirements guide our roadmap.",
        statLabel: 'Patient Support',
        statSublabel: "When care can't wait",
      },
    ],
    comparison: [
      {
        metric: 'Patient Privacy',
        agent5: 'Encrypted, PII redaction',
        traditional: 'Basic encryption',
      },
      {
        metric: 'Appointment Scheduling',
        agent5: 'Instant booking & changes',
        traditional: 'Phone-only, long waits',
      },
      {
        metric: 'Insurance Verification',
        agent5: 'Real-time eligibility checks',
        traditional: 'Manual lookups',
      },
      {
        metric: 'After-Hours Support',
        agent5: '24/7 availability',
        traditional: 'Voicemail or emergencies only',
      },
      {
        metric: 'Prescription Refills',
        agent5: 'Automated requests & tracking',
        traditional: 'Phone tag with pharmacy',
      },
      {
        metric: 'Language Support',
        agent5: '100+ languages instantly',
        traditional: 'Limited translator access',
      },
      {
        metric: 'Response Time',
        agent5: 'Under 10 seconds',
        traditional: '15-30 minute holds',
      },
      {
        metric: 'Cost per Interaction',
        agent5: '$0.75-$0.90 per resolution',
        traditional: '$8-20 per call',
      },
    ],
    comparisonTitle: 'Why healthcare providers choose Agent 5',
    comparisonSubtitle:
      "Purpose-built for healthcare's unique combination of urgency, privacy, and complexity.",
    colWhatMatters: 'What Matters in Healthcare',
    colProduct: 'Agent 5',
    colTraditional: 'Traditional Solutions',
    faqTitle: 'Questions from healthcare compliance teams',
    faqSubtitle:
      'Honest answers about where we are with healthcare compliance.',
    faq: INDUSTRY_FAQS['healthcare'].en,
  },
  ar: {
    heroTitleA: 'رعاية',
    heroTitleB: 'ما تنتهي بنهاية الدوام',
    heroSubtitle:
      'موعد يتغيّر بعد منتصف الليل، سؤال عن تغطية تأمين، وصفة تحتاج تجديداً. يجيب النظام مرضاك ٢٤/٧ وبأمان، ويتولّى عن فريقك العمل الإداري عشان يتفرّغ للرعاية.',
    heroCta: 'ورّيني كيف يشتغل في الرعاية الصحية',
    benefitsTitle: 'دعم أفضل لمرضاك، وخصوصية محفوظة',
    benefitsSubtitle:
      'تعامل مع أهم مسائل الدعم في الرعاية الصحية — بأمان وامتثال.',
    benefits: [
      {
        title: 'حجز المواعيد وتعديلها',
        description:
          'يحجز المريض موعده، ويطلب تعديله، ويستقبل تذكيره — فوراً وفي أي وقت. ويتكامل النظام مع نظام الجدولة عندك. لا مزيد من تبادل المكالمات بلا جدوى.',
      },
      {
        title: 'أهلية التأمين وأسئلة التغطية',
        description:
          'هل هذا الإجراء مغطّى؟ كم نصيبي من التكلفة؟ مين يقبل تأميني؟ يتحقق النظام من التغطية لحظياً، فيعرف المريض قبل ما يحجز.',
      },
      {
        title: 'تجديد الوصفات وأسئلة الأدوية',
        description:
          'طلب تجديد وصفة، سؤال عن حالتها، استفسار عن الجرعة أو أثر جانبي. النظام يعالج الطلب، يتابع حالته، ويصعّد كل سؤال طبي لمن يخصّه.',
      },
      {
        title: 'استفسارات الفواتير وأسئلة الدفع',
        description:
          'وش يغطّي تأميني؟ وليش هذا المبلغ عليّ؟ ومتى آخر موعد للسداد؟ يشرح النظام الفاتورة بنداً بنداً، ويعالج الدفع، ويرتّب خطة تقسيط — فواتير أوضح، ومكالمات أقل.',
      },
      {
        title: 'نتائج الفحوصات ومتابعة الرعاية',
        description:
          'هل ظهرت نتائجي؟ ووش الخطوة التالية؟ ينبّه النظام المريض فور صدور النتيجة، ويشرح ما يليها، ويحجز موعد المتابعة — فما تتوقف الرعاية عند الانتظار.',
      },
      {
        title: 'دليل مقدّمي الخدمة والإحالات',
        description:
          'ساعد مرضاك على إيجاد الأخصائي المناسب، ومعرفة مواعيده المتاحة، وفهم متطلبات الإحالة. يوصّلهم النظام للرعاية الصحيحة أسرع.',
      },
      {
        title: 'الخصوصية والأمان من الأساس',
        description:
          'جميع بيانات المرضى مشفّرة. اكتشاف تلقائي للمعلومات الصحية المحمية (PHI). سجلّات تدقيق كاملة. نعمل على التوافق مع HIPAA، ومتطلبات الأمان لديك هي ما يوجّه خارطة طريقنا.',
        statLabel: 'دعم المرضى',
        statSublabel: 'لمّا ما تحتمل الرعاية الانتظار',
      },
    ],
    comparison: [
      {
        metric: 'خصوصية المرضى',
        agent5: 'مشفّرة، مع تنقيح بيانات الهوية',
        traditional: 'تشفير أساسي',
      },
      {
        metric: 'حجز المواعيد',
        agent5: 'حجز وتعديل فوري',
        traditional: 'عبر الهاتف بس، وانتظار طويل',
      },
      {
        metric: 'التحقق من التأمين',
        agent5: 'تحقق لحظي من الأهلية',
        traditional: 'بحث يدوي',
      },
      {
        metric: 'الدعم بعد الدوام',
        agent5: 'متاح ٢٤/٧',
        traditional: 'بريد صوتي أو للطوارئ بس',
      },
      {
        metric: 'تجديد الوصفات',
        agent5: 'طلب وتتبّع تلقائي',
        traditional: 'ملاحقة مكالمات مع الصيدلية',
      },
      {
        metric: 'دعم اللغات',
        agent5: '+١٠٠ لغة فوراً',
        traditional: 'وصول محدود للمترجمين',
      },
      {
        metric: 'وقت الاستجابة',
        agent5: 'أقل من ١٠ ثوانٍ',
        traditional: 'انتظار ١٥-٣٠ دقيقة',
      },
      {
        metric: 'التكلفة لكل تفاعل',
        agent5: '٠,٩٩ دولار لكل حل',
        traditional: '٨-٢٠ دولاراً لكل مكالمة',
      },
    ],
    comparisonTitle: 'ليش يختار مقدّمو الرعاية الصحية النظام',
    comparisonSubtitle:
      'مبني خصيصاً لقطاع يجتمع فيه الاستعجال والخصوصية والتعقيد في وقت واحد.',
    colWhatMatters: 'اللي يهم في قطاع الرعاية الصحية',
    colProduct: 'النظام',
    colTraditional: 'الحلول التقليدية',
    faqTitle: 'أسئلة من فرق الامتثال في الرعاية الصحية',
    faqSubtitle: 'إجابات صادقة عن موقعنا الحالي من الامتثال في الرعاية الصحية.',
    faq: INDUSTRY_FAQS['healthcare'].ar,
  },
} as const;

export default function HealthcarePage() {
  const { locale } = useTranslation();
  const c = locale === 'ar' ? CONTENT.ar : CONTENT.en;
  const [hoveredModel, setHoveredModel] = useState<string | null>(null);

  const comparisonStatus = [
    { agent5: 'best', traditional: 'worst' },
    { agent5: 'best', traditional: 'worst' },
    { agent5: 'best', traditional: 'worst' },
    { agent5: 'best', traditional: 'worst' },
    { agent5: 'best', traditional: 'worst' },
    { agent5: 'best', traditional: 'neutral' },
    { agent5: 'best', traditional: 'worst' },
    { agent5: 'best', traditional: 'worst' },
  ];

  const comparisonData = c.comparison.map((row, i) => ({
    metric: row.metric,
    agent5: { value: row.agent5, status: comparisonStatus[i].agent5 },
    traditional: {
      value: row.traditional,
      status: comparisonStatus[i].traditional,
    },
  }));

  return (
    <>
      <HelpdeskHero
        title={
          <>
            {c.heroTitleA}
            <br />
            {c.heroTitleB}
          </>
        }
        subtitle={c.heroSubtitle}
        ctaText={c.heroCta}
      />

      <Logos />

      <BenefitsSection
        title={c.benefitsTitle}
        subtitle={c.benefitsSubtitle}
        learnMoreLink="#faq"
        accentColor="emerald"
        backgroundColor="#000000"
        benefits={[
          {
            title: c.benefits[0].title,
            description: c.benefits[0].description,
            linkText: '',
            linkHref: '#',
            imageUrl:
              'https://deifkwefumgah.cloudfront.net/shadcnblocks/block/placeholder-3.svg',
            imagePosition: 'right',
          },
          {
            title: c.benefits[1].title,
            description: c.benefits[1].description,
            linkText: '',
            linkHref: '#',
            imageUrl:
              'https://deifkwefumgah.cloudfront.net/shadcnblocks/block/placeholder-3.svg',
            imagePosition: 'left',
          },
          {
            title: c.benefits[2].title,
            description: c.benefits[2].description,
            linkText: '',
            linkHref: '#',
            imageUrl:
              'https://deifkwefumgah.cloudfront.net/shadcnblocks/block/placeholder-3.svg',
            imagePosition: 'right',
          },
          {
            title: c.benefits[3].title,
            description: c.benefits[3].description,
            linkText: '',
            linkHref: '#',
            imageUrl:
              'https://deifkwefumgah.cloudfront.net/shadcnblocks/block/placeholder-3.svg',
            imagePosition: 'left',
          },
          {
            title: c.benefits[4].title,
            description: c.benefits[4].description,
            linkText: '',
            linkHref: '#',
            imageUrl:
              'https://deifkwefumgah.cloudfront.net/shadcnblocks/block/placeholder-3.svg',
            imagePosition: 'right',
          },
          {
            title: c.benefits[5].title,
            description: c.benefits[5].description,
            linkText: '',
            linkHref: '#',
            imageUrl:
              'https://deifkwefumgah.cloudfront.net/shadcnblocks/block/placeholder-3.svg',
            imagePosition: 'left',
          },
          {
            title: c.benefits[6].title,
            description: c.benefits[6].description,
            linkText: '',
            linkHref: '#',
            imagePosition: 'right',
            statCard: {
              value: '24/7',
              label: c.benefits[6].statLabel,
              sublabel: c.benefits[6].statSublabel,
            },
          },
        ]}
      />

      {/* Comparison Section */}
      <section className="bg-white py-32">
        <div className="container mx-auto">
          <div className="mx-auto mb-16 flex max-w-3xl flex-col justify-center gap-7 text-center md:text-center">
            <h2 className="font-serif text-2xl md:text-4xl">
              {c.comparisonTitle}
            </h2>
            <p className="text-muted-foreground text-sm md:text-base">
              {c.comparisonSubtitle}
            </p>
          </div>
          <div className="relative overflow-hidden p-8">
            <div className="border-border/50 bg-background/50 relative overflow-hidden border backdrop-blur-sm">
              <Table>
                <TableHeader>
                  <TableRow className="border-border/50">
                    <TableHead className="text-foreground font-semibold">
                      {c.colWhatMatters}
                    </TableHead>
                    <TableHead className="text-foreground text-center font-semibold">
                      {c.colProduct}
                    </TableHead>
                    <TableHead className="text-foreground text-center font-semibold">
                      {c.colTraditional}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {comparisonData.map((row, index) => (
                    <TableRow
                      key={index}
                      className="border-border/50"
                      onMouseEnter={() => setHoveredModel(row.metric)}
                      onMouseLeave={() => setHoveredModel(null)}
                    >
                      <TableCell className="text-foreground font-medium">
                        {row.metric}
                      </TableCell>
                      <TableCell className="text-center">
                        <div
                          className={cn(
                            'mx-auto inline-flex items-center gap-2 rounded-full px-3 py-1 transition-all',
                            hoveredModel === row.metric
                              ? 'bg-green-100'
                              : 'bg-transparent',
                          )}
                        >
                          {row.agent5.status === 'best' && (
                            <CheckCircle className="size-4 text-green-600" />
                          )}
                          {row.agent5.status === 'neutral' && (
                            <Minus className="size-4 text-yellow-600" />
                          )}
                          <span className="text-foreground text-sm">
                            {row.agent5.value}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <div
                          className={cn(
                            'mx-auto inline-flex items-center gap-2 rounded-full px-3 py-1 transition-all',
                            hoveredModel === row.metric
                              ? 'bg-red-100'
                              : 'bg-transparent',
                          )}
                        >
                          {row.traditional.status === 'worst' && (
                            <XCircle className="size-4 text-red-600" />
                          )}
                          {row.traditional.status === 'neutral' && (
                            <Minus className="size-4 text-yellow-600" />
                          )}
                          <span className="text-muted-foreground text-sm">
                            {row.traditional.value}
                          </span>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <FaqSection
        title={c.faqTitle}
        subtitle={c.faqSubtitle}
        items={c.faq.map((item) => ({ question: item.q, answer: item.a }))}
      />

      <Testimonials />
    </>
  );
}
